import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, ChevronDown, ChevronUp, Settings, Bird } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatDate } from '@/utils/dateFormat';
import { useCurrency } from '@/contexts/CurrencyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const CHICKEN_TYPES = [
  { value: 'layers', label: 'Layers' },
  { value: 'broilers', label: 'Broilers' },
  { value: 'cockerels', label: 'Cockerels' },
  { value: 'pullets', label: 'Pullets' }
];

const BATCH_STATUS = [
  { value: 'active', label: 'Active' },
  { value: 'sold', label: 'Sold' }
];

// All available pens (1-6 regular, 7 Brooding, 8 Grower)
const PEN_OPTIONS = [
  { value: 'Pen 1', label: 'Pen 1' },
  { value: 'Pen 2', label: 'Pen 2' },
  { value: 'Pen 3', label: 'Pen 3' },
  { value: 'Pen 4', label: 'Pen 4' },
  { value: 'Pen 5', label: 'Pen 5' },
  { value: 'Pen 6', label: 'Pen 6' },
  { value: 'Brooding Pen', label: 'Brooding Pen' },
  { value: 'Grower Pen', label: 'Grower Pen' }
];

const ChickenInventory = () => {
  const [batches, setBatches] = useState([]);
  const [pens, setPens] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedBatch, setExpandedBatch] = useState(null);
  const [activeTab, setActiveTab] = useState({});
  const { currencySymbol, formatCurrency } = useCurrency();
  
  // Get user info for role checking
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user?.role === 'admin';
  
  // Dialogs
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [penDialogOpen, setPenDialogOpen] = useState(false);
  const [immunizationDialogOpen, setImmunizationDialogOpen] = useState(false);
  const [mortalityDialogOpen, setMortalityDialogOpen] = useState(false);
  
  // Form states
  const [editingBatch, setEditingBatch] = useState(null);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  
  const [batchForm, setBatchForm] = useState({
    batch_number: '',
    chicken_type: 'layers',
    breed_type: '',
    date_purchased: new Date().toISOString().split('T')[0],
    seller: '',
    quantity_purchased: '',
    price_per_chicken: '',
    age_at_purchase: '',
    current_pen: '',
    debeaking_dates: ['', ''],  // Support for 2 de-beaking dates
    total_mortality: '',  // Admin can edit this
    status: 'active',
    notes: ''
  });
  
  const [penForm, setPenForm] = useState({
    pen_number: '',
    description: '',
    capacity: ''
  });
  
  const [immunizationForm, setImmunizationForm] = useState({
    medication_vaccine: '',
    date_administered: new Date().toISOString().split('T')[0],
    date_completed: '',
    reason: 'scheduled_immunization',
    effectiveness: 'pending',
    dosage: '',
    notes: ''
  });
  
  const [mortalityForm, setMortalityForm] = useState({
    date: new Date().toISOString().split('T')[0],
    count: '',
    cause: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [batchesRes, pensRes, summaryRes] = await Promise.all([
        axios.get(`${API}/chicken-batches`, getAuthHeader()),
        axios.get(`${API}/chicken-pens`, getAuthHeader()),
        axios.get(`${API}/chicken-inventory/summary`, getAuthHeader())
      ]);
      setBatches(batchesRes.data);
      setPens(pensRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to fetch chicken inventory');
    } finally {
      setLoading(false);
    }
  };

  // Batch CRUD
  const handleOpenBatchDialog = (batch = null) => {
    if (batch) {
      setEditingBatch(batch);
      // Handle both old single debeaking_date and new debeaking_dates array
      let debeakingDates = ['', ''];
      if (batch.debeaking_dates && Array.isArray(batch.debeaking_dates)) {
        debeakingDates = [...batch.debeaking_dates, '', ''].slice(0, 2);
      } else if (batch.debeaking_date) {
        debeakingDates = [batch.debeaking_date, ''];
      }
      
      setBatchForm({
        batch_number: batch.batch_number,
        chicken_type: batch.chicken_type || 'layers',
        breed_type: batch.breed_type || '',
        date_purchased: batch.date_purchased,
        seller: batch.seller,
        quantity_purchased: batch.quantity_purchased.toString(),
        price_per_chicken: batch.price_per_chicken.toString(),
        age_at_purchase: batch.age_at_purchase?.toString() || '',
        current_pen: batch.current_pen,
        debeaking_dates: debeakingDates,
        total_mortality: batch.total_mortality?.toString() || '0',
        status: batch.status || 'active',
        notes: batch.notes || ''
      });
    } else {
      setEditingBatch(null);
      setBatchForm({
        batch_number: '',
        chicken_type: 'layers',
        breed_type: '',
        date_purchased: new Date().toISOString().split('T')[0],
        seller: '',
        quantity_purchased: '',
        price_per_chicken: '',
        age_at_purchase: '',
        current_pen: 'Pen 1',
        debeaking_dates: ['', ''],
        total_mortality: '0',
        status: 'active',
        notes: ''
      });
    }
    setBatchDialogOpen(true);
  };

  const handleSaveBatch = async (e) => {
    e.preventDefault();
    try {
      // Filter out empty de-beaking dates
      const debeakingDates = batchForm.debeaking_dates.filter(d => d && d.trim() !== '');
      
      const data = {
        ...batchForm,
        quantity_purchased: parseInt(batchForm.quantity_purchased),
        price_per_chicken: parseFloat(batchForm.price_per_chicken),
        age_at_purchase: batchForm.age_at_purchase ? parseInt(batchForm.age_at_purchase) : 0,
        debeaking_dates: debeakingDates.length > 0 ? debeakingDates : null,
        debeaking_date: debeakingDates.length > 0 ? debeakingDates[0] : null,  // Keep backward compat
        total_mortality: editingBatch && isAdmin ? parseInt(batchForm.total_mortality || 0) : undefined
      };
      
      // Only send total_mortality when editing and user is admin
      if (!editingBatch || !isAdmin) {
        delete data.total_mortality;
      }
      delete data.debeaking_dates_temp;
      
      if (editingBatch) {
        await axios.put(`${API}/chicken-batches/${editingBatch.id}`, data, getAuthHeader());
        toast.success('Batch updated successfully');
      } else {
        await axios.post(`${API}/chicken-batches`, data, getAuthHeader());
        toast.success('Batch created successfully');
      }
      setBatchDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save batch');
    }
  };

  const handleDeleteBatch = async (batchId) => {
    if (!window.confirm('Are you sure you want to delete this batch? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API}/chicken-batches/${batchId}`, getAuthHeader());
      toast.success('Batch deleted successfully');
      setExpandedBatch(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete batch');
    }
  };

  // Pen CRUD
  const handleSavePen = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...penForm,
        capacity: penForm.capacity ? parseInt(penForm.capacity) : null
      };
      await axios.post(`${API}/chicken-pens`, data, getAuthHeader());
      toast.success('Pen created successfully');
      setPenDialogOpen(false);
      setPenForm({ pen_number: '', description: '', capacity: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create pen');
    }
  };

  const handleDeletePen = async (penId) => {
    if (!window.confirm('Are you sure you want to delete this pen?')) return;
    try {
      await axios.delete(`${API}/chicken-pens/${penId}`, getAuthHeader());
      toast.success('Pen deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete pen');
    }
  };

  // Immunization
  const [editingImmunization, setEditingImmunization] = useState(null);
  
  const handleAddImmunization = async (e) => {
    e.preventDefault();
    try {
      if (editingImmunization) {
        // Update existing immunization
        await axios.put(
          `${API}/chicken-batches/${selectedBatchId}/immunizations/${editingImmunization.id}`,
          immunizationForm,
          getAuthHeader()
        );
        toast.success('Immunization record updated');
        setEditingImmunization(null);
      } else {
        // Add new immunization
        await axios.post(`${API}/chicken-batches/${selectedBatchId}/immunizations`, immunizationForm, getAuthHeader());
        toast.success('Immunization record added');
      }
      setImmunizationDialogOpen(false);
      resetImmunizationForm();
      fetchData();
    } catch (error) {
      toast.error(editingImmunization ? 'Failed to update immunization record' : 'Failed to add immunization record');
    }
  };

  const resetImmunizationForm = () => {
    setImmunizationForm({
      medication_vaccine: '',
      date_administered: new Date().toISOString().split('T')[0],
      date_completed: '',
      reason: 'scheduled_immunization',
      effectiveness: 'pending',
      dosage: '',
      notes: ''
    });
  };

  const handleEditImmunization = (batchId, record) => {
    setSelectedBatchId(batchId);
    setEditingImmunization(record);
    setImmunizationForm({
      medication_vaccine: record.medication_vaccine || '',
      date_administered: record.date_administered || new Date().toISOString().split('T')[0],
      date_completed: record.date_completed || '',
      reason: record.reason || 'scheduled_immunization',
      effectiveness: record.effectiveness || 'pending',
      dosage: record.dosage || '',
      notes: record.notes || ''
    });
    setImmunizationDialogOpen(true);
  };

  const handleDeleteImmunization = async (batchId, immunizationId) => {
    if (!window.confirm('Delete this immunization record?')) return;
    try {
      await axios.delete(`${API}/chicken-batches/${batchId}/immunizations/${immunizationId}`, getAuthHeader());
      toast.success('Immunization record deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete immunization record');
    }
  };

  // Mortality
  const handleAddMortality = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...mortalityForm,
        count: parseInt(mortalityForm.count)
      };
      await axios.post(`${API}/chicken-batches/${selectedBatchId}/mortality`, data, getAuthHeader());
      toast.success('Mortality record added');
      setMortalityDialogOpen(false);
      setMortalityForm({ date: new Date().toISOString().split('T')[0], count: '', cause: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add mortality record');
    }
  };

  const handleDeleteMortality = async (batchId, mortalityId) => {
    if (!window.confirm('Delete this mortality record?')) return;
    try {
      await axios.delete(`${API}/chicken-batches/${batchId}/mortality/${mortalityId}`, getAuthHeader());
      toast.success('Mortality record deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete mortality record');
    }
  };

  const getChickenTypeLabel = (value) => CHICKEN_TYPES.find(t => t.value === value)?.label || value;

  const getMortalityPercentage = (batch) => {
    if (!batch.quantity_purchased || batch.quantity_purchased === 0) return '0.0';
    return ((batch.total_mortality / batch.quantity_purchased) * 100).toFixed(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading chicken inventory...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Chicken Demographic Records</h1>
          <p className="text-slate-600">Manage chicken batches, mortality, census, and medication/vaccination records</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setPenDialogOpen(true)} data-testid="manage-pens-btn">
            <Settings className="w-4 h-4 mr-2" />
            Manage Pens
          </Button>
          <Button onClick={() => handleOpenBatchDialog()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-batch-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Chicken Batch
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg border p-4">
            <div className="text-sm text-slate-500">Active Batches</div>
            <div className="text-2xl font-bold text-emerald-600">{summary.active_batches || 0}</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-sm text-slate-500">Sold Batches</div>
            <div className="text-2xl font-bold text-orange-600">{summary.sold_batches || 0}</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-sm text-slate-500">Total Chickens</div>
            <div className="text-2xl font-bold text-blue-600">{summary.total_chickens?.toLocaleString() || 0}</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-sm text-slate-500">Total Mortality</div>
            <div className="text-2xl font-bold text-red-600">{summary.total_mortality?.toLocaleString() || 0}</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-sm text-slate-500">Total Investment</div>
            <div className="text-2xl font-bold text-slate-800">{currencySymbol}{formatCurrency(summary.total_investment || 0)}</div>
          </div>
        </div>
      )}

      {/* Census Mortality Summary - from daily census records */}
      {summary?.census_mortality_by_pen && summary.total_census_mortality > 0 && (
        <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-lg border border-red-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-red-800">Census Mortality Summary</h3>
            <div className="text-sm text-red-600 bg-red-100 px-3 py-1 rounded-full">
              Total: {summary.total_census_mortality?.toLocaleString()} birds
            </div>
          </div>
          <p className="text-sm text-red-700 mb-3">
            Cumulative mortality recorded through daily Chicken Census entries
          </p>
          <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
            {Object.entries(summary.census_mortality_by_pen).map(([penName, count]) => (
              <div 
                key={penName} 
                className={`p-2 rounded text-center ${count > 0 ? 'bg-red-100 border border-red-200' : 'bg-white border border-slate-200'}`}
              >
                <div className="text-xs text-slate-500 truncate">{penName.replace('Pen ', 'P')}</div>
                <div className={`text-lg font-bold ${count > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                  {count}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Batches List */}
      <div className="bg-white rounded-lg border">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold text-slate-800">Chicken Batches</h2>
        </div>
        
        {batches.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <Bird className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No chicken batches yet. Click "New Chicken Batch" to create one.</p>
          </div>
        ) : (
          <div className="divide-y">
            {batches.map(batch => (
              <div key={batch.id} className={`${batch.status === 'sold' ? 'bg-orange-50/30' : ''}`}>
                {/* Batch Header */}
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                  onClick={() => {
                    setExpandedBatch(expandedBatch === batch.id ? null : batch.id);
                    if (!activeTab[batch.id]) {
                      setActiveTab({...activeTab, [batch.id]: 'overview'});
                    }
                  }}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                      <Bird className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-slate-800">{batch.batch_number}</span>
                        <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800">{getChickenTypeLabel(batch.chicken_type)}</span>
                        {batch.breed_type && <span className="text-slate-500">· {batch.breed_type}</span>}
                        <span className={`text-xs px-2 py-0.5 rounded ${batch.status === 'sold' ? 'bg-orange-100 text-orange-700' : 'bg-emerald-100 text-emerald-700'}`}>
                          {batch.status === 'sold' ? 'Sold' : 'Active'}
                        </span>
                      </div>
                      <div className="text-sm text-slate-500">
                        Purchased: {formatDate(batch.date_purchased)} &nbsp; Pen: <span className="font-medium">{batch.current_pen}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right hidden sm:block">
                      <div className="flex items-center gap-4 text-sm">
                        <div>
                          <div className="text-slate-500">Initial</div>
                          <div className="font-semibold">{batch.quantity_purchased?.toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-red-500">Mortality</div>
                          <div className="font-semibold text-red-600">{batch.total_mortality?.toLocaleString() || 0}</div>
                        </div>
                        <div>
                          <div className="text-emerald-500">Current</div>
                          <div className="font-semibold text-emerald-600">{batch.current_census?.toLocaleString()}</div>
                        </div>
                      </div>
                    </div>
                    {expandedBatch === batch.id ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                  </div>
                </div>
                
                {/* Expanded Content with Tabs */}
                {expandedBatch === batch.id && (
                  <div className="border-t bg-slate-50">
                    {/* Tabs Header */}
                    <div className="flex items-center justify-between px-4 pt-2 border-b bg-white">
                      <div className="flex">
                        <button 
                          className={`px-4 py-3 text-sm font-medium border-b-2 ${activeTab[batch.id] === 'overview' ? 'border-emerald-600 text-emerald-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                          onClick={() => setActiveTab({...activeTab, [batch.id]: 'overview'})}
                        >
                          Overview
                        </button>
                        <button 
                          className={`px-4 py-3 text-sm font-medium border-b-2 ${activeTab[batch.id] === 'mortality' ? 'border-emerald-600 text-emerald-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                          onClick={() => setActiveTab({...activeTab, [batch.id]: 'mortality'})}
                        >
                          Mortality ({batch.mortality_records?.length || 0})
                        </button>
                        <button 
                          className={`px-4 py-3 text-sm font-medium border-b-2 ${activeTab[batch.id] === 'immunization' ? 'border-emerald-600 text-emerald-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                          onClick={() => setActiveTab({...activeTab, [batch.id]: 'immunization'})}
                        >
                          Medication/Vaccination ({batch.immunizations?.length || 0})
                        </button>
                      </div>
                      <div className="flex items-center gap-2 pb-2">
                        <button 
                          className="p-2 text-slate-400 hover:text-slate-600"
                          onClick={() => handleOpenBatchDialog(batch)}
                          data-testid="edit-batch-btn"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button 
                          className="p-2 text-slate-400 hover:text-red-600"
                          onClick={() => handleDeleteBatch(batch.id)}
                          data-testid="delete-batch-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Tab Content */}
                    <div className="p-4">
                      {/* Overview Tab */}
                      {activeTab[batch.id] === 'overview' && (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          <div className="p-4 bg-white rounded-lg border">
                            <div className="text-sm text-slate-500">Batch Number</div>
                            <div className="font-semibold text-slate-800">{batch.batch_number}</div>
                          </div>
                          <div className="p-4 bg-white rounded-lg border">
                            <div className="text-sm text-slate-500">Chicken Type</div>
                            <div className="font-semibold text-slate-800">{getChickenTypeLabel(batch.chicken_type)}</div>
                          </div>
                          <div className="p-4 bg-white rounded-lg border">
                            <div className="text-sm text-slate-500">Breed Type</div>
                            <div className="font-semibold text-slate-800">{batch.breed_type || '—'}</div>
                          </div>
                          <div className="p-4 bg-white rounded-lg border">
                            <div className="text-sm text-slate-500">Seller / Company</div>
                            <div className="font-semibold text-slate-800">{batch.seller}</div>
                          </div>
                          <div className="p-4 bg-white rounded-lg border">
                            <div className="text-sm text-slate-500">Date Purchased</div>
                            <div className="font-semibold text-slate-800">{formatDate(batch.date_purchased)}</div>
                          </div>
                          <div className="p-4 bg-white rounded-lg border">
                            <div className="text-sm text-slate-500">Quantity Purchased</div>
                            <div className="font-semibold text-slate-800">{batch.quantity_purchased?.toLocaleString()}</div>
                          </div>
                          <div className="p-4 bg-white rounded-lg border">
                            <div className="text-sm text-slate-500">Price per Chicken</div>
                            <div className="font-semibold text-slate-800">{currencySymbol}{formatCurrency(batch.price_per_chicken)}</div>
                          </div>
                          <div className="p-4 bg-white rounded-lg border">
                            <div className="text-sm text-slate-500">Total Purchase Cost</div>
                            <div className="font-semibold text-slate-800">{currencySymbol}{formatCurrency(batch.quantity_purchased * batch.price_per_chicken)}</div>
                          </div>
                          <div className="p-4 bg-white rounded-lg border">
                            <div className="text-sm text-slate-500">Age at Purchase</div>
                            <div className="font-semibold text-slate-800">{batch.age_at_purchase || 0} weeks</div>
                          </div>
                          <div className="p-4 bg-white rounded-lg border">
                            <div className="text-sm text-slate-500">Current Pen</div>
                            <div className="font-semibold text-slate-800">{batch.current_pen}</div>
                          </div>
                          <div className="p-4 bg-white rounded-lg border col-span-2">
                            <div className="text-sm text-slate-500">De-Beaking Dates</div>
                            <div className="font-semibold text-slate-800">
                              {batch.debeaking_dates && batch.debeaking_dates.length > 0 ? (
                                batch.debeaking_dates.map((date, idx) => (
                                  <span key={idx} className="mr-3">
                                    #{idx + 1}: {formatDate(date)}
                                  </span>
                                ))
                              ) : batch.debeaking_date ? (
                                formatDate(batch.debeaking_date)
                              ) : '—'}
                            </div>
                          </div>
                          <div className="p-4 bg-white rounded-lg border">
                            <div className="text-sm text-slate-500">Total Mortality</div>
                            <div className="font-semibold text-red-600">{batch.total_mortality || 0} ({getMortalityPercentage(batch)}%)</div>
                          </div>
                          <div className="p-4 bg-white rounded-lg border">
                            <div className="text-sm text-slate-500">Current Census</div>
                            <div className="font-semibold text-emerald-600">{batch.current_census?.toLocaleString()}</div>
                          </div>
                          <div className="p-4 bg-white rounded-lg border">
                            <div className="text-sm text-slate-500">Status</div>
                            <div className="font-semibold text-slate-800">{batch.status}</div>
                          </div>
                        </div>
                      )}
                      
                      {/* Mortality Tab */}
                      {activeTab[batch.id] === 'mortality' && (
                        <div>
                          {/* Summary Cards */}
                          <div className="grid grid-cols-3 gap-4 mb-6">
                            <div className="p-6 bg-white rounded-lg border text-center">
                              <div className="text-sm text-slate-500 mb-2">Initial Census</div>
                              <div className="text-2xl font-bold text-slate-800">{batch.quantity_purchased?.toLocaleString()}</div>
                            </div>
                            <div className="p-6 bg-red-50 rounded-lg border border-red-100 text-center">
                              <div className="text-sm text-red-500 mb-2">Total Mortality</div>
                              <div className="text-2xl font-bold text-red-600">{batch.total_mortality || 0}</div>
                            </div>
                            <div className="p-6 bg-emerald-50 rounded-lg border border-emerald-100 text-center">
                              <div className="text-sm text-emerald-500 mb-2">Current Census</div>
                              <div className="text-2xl font-bold text-emerald-600">{batch.current_census?.toLocaleString()}</div>
                            </div>
                          </div>
                          
                          {/* Add Mortality Button */}
                          <Button 
                            variant="outline" 
                            className="mb-4"
                            onClick={() => { setSelectedBatchId(batch.id); setMortalityDialogOpen(true); }}
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Record Mortality
                          </Button>
                          
                          {/* Mortality Records */}
                          {batch.mortality_records?.length > 0 ? (
                            <div className="space-y-2">
                              {batch.mortality_records.map(record => (
                                <div key={record.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                                  <div>
                                    <div className="font-semibold text-red-600">-{record.count} chickens</div>
                                    <div className="text-sm text-slate-500">{formatDate(record.date)}</div>
                                    {record.cause && <div className="text-sm text-slate-600">Cause: {record.cause}</div>}
                                    {record.notes && <div className="text-xs text-slate-400">{record.notes}</div>}
                                  </div>
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    onClick={() => handleDeleteMortality(batch.id, record.id)}
                                  >
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-center text-slate-400 py-8">No mortality records yet.</p>
                          )}
                        </div>
                      )}
                      
                      {/* Immunization Tab */}
                      {activeTab[batch.id] === 'immunization' && (
                        <div>
                          {/* Add Immunization Button */}
                          <Button 
                            variant="outline" 
                            className="mb-4"
                            onClick={() => { 
                              setSelectedBatchId(batch.id); 
                              setEditingImmunization(null);
                              resetImmunizationForm();
                              setImmunizationDialogOpen(true); 
                            }}
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Medication/Vaccination Record
                          </Button>
                          
                          {/* Immunization Records */}
                          {batch.immunizations?.length > 0 ? (
                            <div className="space-y-3">
                              {batch.immunizations.map(record => (
                                <div key={record.id} className="p-4 bg-white rounded-lg border">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="font-semibold text-slate-800 text-lg">{record.medication_vaccine}</div>
                                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-sm">
                                        <div>
                                          <span className="text-slate-500">Started:</span>
                                          <span className="ml-1 font-medium">{formatDate(record.date_administered)}</span>
                                        </div>
                                        {record.date_completed && (
                                          <div>
                                            <span className="text-slate-500">Completed:</span>
                                            <span className="ml-1 font-medium">{formatDate(record.date_completed)}</span>
                                          </div>
                                        )}
                                        <div>
                                          <span className="text-slate-500">Reason:</span>
                                          <span className={`ml-1 px-2 py-0.5 rounded text-xs font-medium ${
                                            record.reason === 'treatment' ? 'bg-red-100 text-red-700' :
                                            record.reason === 'disease_outbreak' ? 'bg-orange-100 text-orange-700' :
                                            record.reason === 'scheduled_immunization' ? 'bg-green-100 text-green-700' :
                                            'bg-blue-100 text-blue-700'
                                          }`}>
                                            {record.reason === 'treatment' ? 'Treatment' :
                                             record.reason === 'disease_outbreak' ? 'Disease Outbreak' :
                                             record.reason === 'scheduled_immunization' ? 'Scheduled Immunization' :
                                             'Preventive'}
                                          </span>
                                        </div>
                                        <div>
                                          <span className="text-slate-500">Effectiveness:</span>
                                          <span className={`ml-1 px-2 py-0.5 rounded text-xs font-medium ${
                                            record.effectiveness === 'effective' ? 'bg-green-100 text-green-700' :
                                            record.effectiveness === 'partially_effective' ? 'bg-yellow-100 text-yellow-700' :
                                            record.effectiveness === 'not_effective' ? 'bg-red-100 text-red-700' :
                                            record.effectiveness === 'pending' ? 'bg-slate-100 text-slate-700' :
                                            'bg-slate-100 text-slate-500'
                                          }`}>
                                            {record.effectiveness === 'effective' ? 'Effective' :
                                             record.effectiveness === 'partially_effective' ? 'Partially Effective' :
                                             record.effectiveness === 'not_effective' ? 'Not Effective' :
                                             record.effectiveness === 'pending' ? 'Pending' :
                                             'N/A'}
                                          </span>
                                        </div>
                                      </div>
                                      {record.dosage && <div className="text-sm text-slate-600 mt-1">Dosage: {record.dosage}</div>}
                                      {record.notes && <div className="text-sm text-slate-500 mt-1 italic">{record.notes}</div>}
                                    </div>
                                    <div className="flex gap-1 ml-2">
                                      <Button 
                                        size="sm" 
                                        variant="ghost" 
                                        onClick={() => handleEditImmunization(batch.id, record)}
                                      >
                                        <Edit className="w-4 h-4 text-blue-500" />
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        variant="ghost" 
                                        onClick={() => handleDeleteImmunization(batch.id, record.id)}
                                      >
                                        <Trash2 className="w-4 h-4 text-red-500" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-center text-slate-400 py-8">No medication/vaccination records yet.</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New Chicken Batch Dialog */}
      <Dialog open={batchDialogOpen} onOpenChange={setBatchDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingBatch ? 'Edit Chicken Batch' : 'New Chicken Batch'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveBatch} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Batch Number *</Label>
                <Input
                  value={batchForm.batch_number}
                  onChange={e => setBatchForm({...batchForm, batch_number: e.target.value})}
                  placeholder="e.g. BATCH-001"
                  required
                  data-testid="batch-number-input"
                />
              </div>
              <div>
                <Label>Chicken Type</Label>
                <Select value={batchForm.chicken_type} onValueChange={v => setBatchForm({...batchForm, chicken_type: v})}>
                  <SelectTrigger data-testid="chicken-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {CHICKEN_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Breed Type</Label>
                <Input
                  value={batchForm.breed_type}
                  onChange={e => setBatchForm({...batchForm, breed_type: e.target.value})}
                  placeholder="e.g. ISA Brown"
                  data-testid="breed-type-input"
                />
              </div>
              <div>
                <Label>Seller / Company</Label>
                <Input
                  value={batchForm.seller}
                  onChange={e => setBatchForm({...batchForm, seller: e.target.value})}
                  placeholder="e.g. Obaseki Farms Ltd"
                  data-testid="seller-input"
                />
              </div>
              <div>
                <Label>Date Purchased *</Label>
                <Input
                  type="date"
                  value={batchForm.date_purchased}
                  onChange={e => setBatchForm({...batchForm, date_purchased: e.target.value})}
                  required
                  data-testid="date-purchased-input"
                />
              </div>
              <div>
                <Label>Quantity Purchased *</Label>
                <Input
                  type="number"
                  value={batchForm.quantity_purchased}
                  onChange={e => setBatchForm({...batchForm, quantity_purchased: e.target.value})}
                  placeholder="0"
                  min="1"
                  required
                  data-testid="quantity-input"
                />
              </div>
              <div>
                <Label>Price per Chicken ({currencySymbol})</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={batchForm.price_per_chicken}
                  onChange={e => setBatchForm({...batchForm, price_per_chicken: e.target.value})}
                  placeholder="0.00"
                  min="0"
                  data-testid="price-input"
                />
              </div>
              <div>
                <Label>Age at Purchase (weeks)</Label>
                <Input
                  type="number"
                  value={batchForm.age_at_purchase}
                  onChange={e => setBatchForm({...batchForm, age_at_purchase: e.target.value})}
                  placeholder="0"
                  min="0"
                  data-testid="age-input"
                />
              </div>
              <div>
                <Label>Current Pen</Label>
                <Select value={batchForm.current_pen} onValueChange={v => setBatchForm({...batchForm, current_pen: v})}>
                  <SelectTrigger data-testid="pen-select">
                    <SelectValue placeholder="Select pen" />
                  </SelectTrigger>
                  <SelectContent>
                    {PEN_OPTIONS.map(pen => (
                      <SelectItem key={pen.value} value={pen.value}>{pen.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={batchForm.status} onValueChange={v => setBatchForm({...batchForm, status: v})}>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {BATCH_STATUS.map(status => (
                      <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>De-Beaking Date #1</Label>
                <Input
                  type="date"
                  value={batchForm.debeaking_dates[0] || ''}
                  onChange={e => {
                    const newDates = [...batchForm.debeaking_dates];
                    newDates[0] = e.target.value;
                    setBatchForm({...batchForm, debeaking_dates: newDates});
                  }}
                  data-testid="debeaking-date-1-input"
                />
              </div>
              <div>
                <Label>De-Beaking Date #2</Label>
                <Input
                  type="date"
                  value={batchForm.debeaking_dates[1] || ''}
                  onChange={e => {
                    const newDates = [...batchForm.debeaking_dates];
                    newDates[1] = e.target.value;
                    setBatchForm({...batchForm, debeaking_dates: newDates});
                  }}
                  data-testid="debeaking-date-2-input"
                />
              </div>
            </div>
            
            {/* Mortality Edit Section - Admin Only when editing */}
            {editingBatch && isAdmin && (
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <Label className="text-red-700 font-medium">Total Mortality (Admin Only)</Label>
                <p className="text-sm text-red-600 mb-2">
                  Edit this value to reconcile with actual count. Current census will be recalculated automatically.
                </p>
                <div className="flex items-center gap-4">
                  <Input
                    type="number"
                    min="0"
                    value={batchForm.total_mortality}
                    onChange={e => setBatchForm({...batchForm, total_mortality: e.target.value})}
                    className="w-32 border-red-300"
                    data-testid="mortality-edit-input"
                  />
                  <span className="text-sm text-slate-600">
                    Current Census: {parseInt(batchForm.quantity_purchased || 0) - parseInt(batchForm.total_mortality || 0)}
                  </span>
                </div>
              </div>
            )}
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={batchForm.notes}
                onChange={e => setBatchForm({...batchForm, notes: e.target.value})}
                placeholder="Any additional notes..."
                data-testid="notes-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBatchDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-batch-btn">
                {editingBatch ? 'Update Batch' : 'Create Batch'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manage Pens Dialog */}
      <Dialog open={penDialogOpen} onOpenChange={setPenDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Manage Chicken Pens</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="list">
            <TabsList className="w-full">
              <TabsTrigger value="list" className="flex-1">Pen List</TabsTrigger>
              <TabsTrigger value="add" className="flex-1">Add Pen</TabsTrigger>
            </TabsList>
            <TabsContent value="list" className="space-y-2 mt-4">
              {pens.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No pens created yet.</p>
              ) : (
                pens.map(pen => (
                  <div key={pen.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <div className="font-semibold">{pen.pen_number}</div>
                      {pen.description && <div className="text-sm text-slate-500">{pen.description}</div>}
                      {pen.capacity && <div className="text-xs text-slate-400">Capacity: {pen.capacity}</div>}
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => handleDeletePen(pen.id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))
              )}
            </TabsContent>
            <TabsContent value="add" className="mt-4">
              <form onSubmit={handleSavePen} className="space-y-4">
                <div>
                  <Label>Pen Number *</Label>
                  <Input
                    value={penForm.pen_number}
                    onChange={e => setPenForm({...penForm, pen_number: e.target.value})}
                    placeholder="e.g., Pen 1"
                    required
                    data-testid="pen-number-input"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input
                    value={penForm.description}
                    onChange={e => setPenForm({...penForm, description: e.target.value})}
                    placeholder="e.g., Layer House 1"
                  />
                </div>
                <div>
                  <Label>Capacity</Label>
                  <Input
                    type="number"
                    value={penForm.capacity}
                    onChange={e => setPenForm({...penForm, capacity: e.target.value})}
                    placeholder="Maximum chickens"
                    min="1"
                  />
                </div>
                <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="create-pen-btn">Create Pen</Button>
              </form>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Immunization Dialog */}
      <Dialog open={immunizationDialogOpen} onOpenChange={(open) => {
        setImmunizationDialogOpen(open);
        if (!open) {
          setEditingImmunization(null);
          resetImmunizationForm();
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingImmunization ? 'Edit Medication/Vaccination Record' : 'Add Medication/Vaccination Record'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddImmunization} className="space-y-4">
            <div>
              <Label>Medication / Vaccine Name *</Label>
              <Input
                value={immunizationForm.medication_vaccine}
                onChange={e => setImmunizationForm({...immunizationForm, medication_vaccine: e.target.value})}
                placeholder="e.g., Newcastle Disease Vaccine"
                required
                data-testid="medication-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date Started *</Label>
                <Input
                  type="date"
                  value={immunizationForm.date_administered}
                  onChange={e => setImmunizationForm({...immunizationForm, date_administered: e.target.value})}
                  required
                  data-testid="immunization-date-input"
                />
              </div>
              <div>
                <Label>Date Completed</Label>
                <Input
                  type="date"
                  value={immunizationForm.date_completed}
                  onChange={e => setImmunizationForm({...immunizationForm, date_completed: e.target.value})}
                  data-testid="immunization-date-completed-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Reason *</Label>
                <Select 
                  value={immunizationForm.reason} 
                  onValueChange={(value) => setImmunizationForm({...immunizationForm, reason: value})}
                >
                  <SelectTrigger data-testid="immunization-reason-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="treatment">Treatment of Illness</SelectItem>
                    <SelectItem value="disease_outbreak">Disease Outbreak</SelectItem>
                    <SelectItem value="scheduled_immunization">Scheduled Immunization</SelectItem>
                    <SelectItem value="preventive">Preventive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Effectiveness</Label>
                <Select 
                  value={immunizationForm.effectiveness} 
                  onValueChange={(value) => setImmunizationForm({...immunizationForm, effectiveness: value})}
                >
                  <SelectTrigger data-testid="immunization-effectiveness-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="effective">Effective</SelectItem>
                    <SelectItem value="partially_effective">Partially Effective</SelectItem>
                    <SelectItem value="not_effective">Not Effective</SelectItem>
                    <SelectItem value="not_applicable">Not Applicable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Dosage</Label>
              <Input
                value={immunizationForm.dosage}
                onChange={e => setImmunizationForm({...immunizationForm, dosage: e.target.value})}
                placeholder="e.g., 0.5ml per bird"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={immunizationForm.notes}
                onChange={e => setImmunizationForm({...immunizationForm, notes: e.target.value})}
                placeholder="Additional notes..."
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setImmunizationDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-immunization-btn">
                {editingImmunization ? 'Update Record' : 'Add Record'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Mortality Dialog */}
      <Dialog open={mortalityDialogOpen} onOpenChange={setMortalityDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Mortality</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddMortality} className="space-y-4">
            <div>
              <Label>Date *</Label>
              <Input
                type="date"
                value={mortalityForm.date}
                onChange={e => setMortalityForm({...mortalityForm, date: e.target.value})}
                required
                data-testid="mortality-date-input"
              />
            </div>
            <div>
              <Label>Count *</Label>
              <Input
                type="number"
                value={mortalityForm.count}
                onChange={e => setMortalityForm({...mortalityForm, count: e.target.value})}
                placeholder="Number of deaths"
                min="1"
                required
                data-testid="mortality-count-input"
              />
            </div>
            <div>
              <Label>Cause</Label>
              <Input
                value={mortalityForm.cause}
                onChange={e => setMortalityForm({...mortalityForm, cause: e.target.value})}
                placeholder="e.g., Disease, Heat stress"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={mortalityForm.notes}
                onChange={e => setMortalityForm({...mortalityForm, notes: e.target.value})}
                placeholder="Additional notes..."
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setMortalityDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-mortality-btn">Record Mortality</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChickenInventory;
